import { action } from 'typesafe-actions';
import urlUtil from '../util/url/urlUtil';
import history from './HistoryService';
import { WorkspaceTypeKeys } from '../store/workspaces/types';
import { HitData, SearchFilters } from '../store/common/search/types';
import { CommonContextState } from '../store/context/types';
import commonSearchHitService, { AppSpecifiedSearchHitService } from './search/commonSearchHitService';
import { StoreManager } from '../store/typings';
import { NavigationData } from '../features/footer/types';
import commonRoutingService from './commonRoutingService';

export interface AppSpecifiedNavigationListService {
     navigateToWorkspace?: (workspaceId: string, silent?: boolean) => void;
     navigateTo: (hits: HitData, dataType?: string,) => void;
     navigateToCurrentQtiContextItem?: () => void;
     getContextValue: (context: CommonContextState, contextKey: string) => keyof CommonContextState;
     getEmptyHitResponse: (context?: CommonContextState, dataType?: string,) => HitData[];
     getDefaultTabsForDataType: (dataType?: string) => any[];
     getHitValuesFromNavigation: (hits: HitData, dataType?: string,) => any;
     backToOriginalTool?: (context?: CommonContextState) => void;
     backToOriginalLists: (context?: CommonContextState) => void;
     redirectToLogin: () => void;
     getSearchFiltersForContext?: (context: CommonContextState, contextFilters: string[]) => SearchFilters;
     validateContext: (context?: CommonContextState) => boolean;
     openQuestionInEditMode?: (options: QuestionConfig) => void;
}

export interface QuestionConfig {
    contentItemId: number;
    contentType: 'content_question';
    mode: 'question';
    useQuestionEditOnly: boolean;
    itemBankId?: number;
    isTemplate?: boolean;
}

interface CommonNavigationService {
    setSetParamsInSearchString: (paramsToUpdate: any, options: {silent?: boolean; isAuthorCall?: boolean}) => void;
    navigateToPrevious: (context: CommonContextState, hits: HitData[], dataType: string) => void;
    navigateToNext: (context: CommonContextState, hits: HitData[], dataType: string) => void;
    getHitInvalidStatus: (navigationData: NavigationData, dataType: string, selectionValue: any) => boolean;
    getHitsWithFirstAndLast: (navigationData: NavigationData, dataType: string) => HitData[];
    navigateToBoth: (dataType1: string, dataType2: string, hit1: HitData, hit2: HitData) => void;
    getSearchFiltersForContext: (context: CommonContextState, contextFilters: string[]) => SearchFilters;
    navigateTo?: (parameters: {[key: string]: any}, silent?: boolean) => void;
}

let store: StoreManager;
let appSpecifiedNavigationListService: AppSpecifiedNavigationListService;
let appSpecifiedSearchHitService: AppSpecifiedSearchHitService;

const commonNavigationService: CommonNavigationService = {
    setSetParamsInSearchString(paramsToUpdate: any, { silent, isAuthorCall = false }: {silent?: boolean; isAuthorCall?: boolean}): void {
        let historyPushData;
        const parameters: any = {
            ...urlUtil.parseLocationSearchString(window.location.search),
            ...paramsToUpdate,
        };

        if (isAuthorCall) {
            historyPushData = urlUtil.generateLinkForAuthorCall(parameters);
        } else {
            const queryString: string = urlUtil.buildUrlQueryString(parameters);
            historyPushData = `/${queryString}`;
        }

        const state = silent ? { silent: true } : undefined;
        history.push(historyPushData, state);

        if (paramsToUpdate.workspaceId) {
            store.dispatch(action(WorkspaceTypeKeys.WILL_CHANGE_WORKSPACE_ID));
        }
    },

    navigateToPrevious(context: CommonContextState, hits: HitData[], dataType: string) {
        const currentIndex = commonSearchHitService.getCurrentHitIndex(dataType, context, hits);

        if (currentIndex > 0) {
            appSpecifiedNavigationListService.navigateTo(hits[currentIndex - 1], dataType);
        }
    },
    navigateToNext(context: CommonContextState, hits: HitData[], dataType: string) {
        const currentIndex = commonSearchHitService.getCurrentHitIndex(dataType, context, hits);

        if (currentIndex < hits.length - 1) {
            appSpecifiedNavigationListService.navigateTo(hits[currentIndex + 1], dataType);
        }
    },
    getHitInvalidStatus(navigationData: NavigationData, dataType: string, selectionValue: any) {
        const selectionKey: string = appSpecifiedSearchHitService.getHitSelectionKey(dataType);
        const hit = navigationData.hits.find((currentHit: any) => {
            return currentHit[selectionKey] === selectionValue;
        });
        return hit && hit.invalid;
    },
    getHitsWithFirstAndLast(navigationData: NavigationData, dataType: string) {
        // the invalid hit is always the first in the list
        const searchMode: boolean = navigationData.searchText !== '';
        const invalidHit = navigationData.hits.length && navigationData.hits[0].invalid ? navigationData.hits.shift() : null;
        const hitsNavList = navigationData.hits.map((hit: any) => {
            return appSpecifiedSearchHitService.getFooterOption(hit, dataType);
        });
        // We don't know if the first centered hit is the second in the list (in that case we should not add `---`)
        // So we call centered hits +1 and if the first hit is not present we delete the first centered hit.
        // Same logic for the last hit
        if (navigationData.firstHit && navigationData.lastHit && navigationData.hits.length > 1) {
            const firstHitIndex = commonSearchHitService.getHitIndexForValue(
                dataType,
                navigationData.hits,
                appSpecifiedSearchHitService.getHitPrimaryIdValue(navigationData.firstHit, dataType),
            );
            if (firstHitIndex === -1) {
                if (!searchMode) {
                    hitsNavList.shift();
                }
                hitsNavList.unshift({ text: '---', value: null, key: -1 });
                hitsNavList.unshift(appSpecifiedSearchHitService.getFooterOption(navigationData.firstHit, dataType));
            }
            const lastHitIndex = commonSearchHitService.getHitIndexForValue(
                dataType,
                navigationData.hits,
                appSpecifiedSearchHitService.getHitPrimaryIdValue(navigationData.lastHit, dataType),
            );
            if (lastHitIndex === -1
                && !commonSearchHitService.areHitsEqual(dataType, navigationData.firstHit, navigationData.lastHit)) {
                hitsNavList.pop();
                hitsNavList.push({ text: '---', value: null, key: -2 });
                hitsNavList.push(appSpecifiedSearchHitService.getFooterOption(navigationData.lastHit, dataType));
            }
        }
        if (invalidHit) {
            navigationData.hits.unshift(invalidHit);
            hitsNavList.unshift(appSpecifiedSearchHitService.getFooterOption(invalidHit, dataType));
        }

        return hitsNavList;
    },

    navigateToBoth(dataType1: string, dataType2: string, hit1: HitData, hit2: HitData) {
        const newValues = {
            ...appSpecifiedNavigationListService.getHitValuesFromNavigation(hit1, dataType1),
            ...appSpecifiedNavigationListService.getHitValuesFromNavigation(hit2, dataType2),
        };
        commonRoutingService.setNewUrlSearchString(newValues);
    },

    // Navigate to marking workspace to review validation issue question marks
    navigateTo(parameters: {[key: string]: any}, silent?: boolean): void {
        commonNavigationService.setSetParamsInSearchString({ ...parameters }, { silent });
    },
    getSearchFiltersForContext(context: CommonContextState, contextFilters: string[]): SearchFilters {
        const searchFilter: SearchFilters = {};
        if (!contextFilters) {
            return searchFilter;
        }
        contextFilters.forEach((contextKey: string) => {
            searchFilter[contextKey] = appSpecifiedNavigationListService.getContextValue(context, contextKey);
        });
        return searchFilter;
    },

};

export const initializeCommonNavigationService = (appStore: StoreManager,
    appNavigationListService: AppSpecifiedNavigationListService,
    appSearchHitService: AppSpecifiedSearchHitService): void => {
    store = appStore;
    appSpecifiedNavigationListService = appNavigationListService;
    appSpecifiedSearchHitService = appSearchHitService;
};

export const getAppSpecifiedNavigationListService = (): AppSpecifiedNavigationListService => {
    return appSpecifiedNavigationListService;
};

export default commonNavigationService;
